/* Generic CRUD screens for simpler entities: services, blog, testimonials, faqs, team, instagram. */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

function useCrud(endpoint) {
  const [items, setItems] = useState([]);
  const load = () => api.get(`/admin/${endpoint}`).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const save = async (body) => {
    try {
      if (body.id) await api.put(`/admin/${endpoint}/${body.id}`, body);
      else await api.post(`/admin/${endpoint}`, body);
      toast.success("Saved"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => { await api.delete(`/admin/${endpoint}/${id}`); toast.success("Deleted"); load(); };
  return { items, load, save, del };
}

function CrudScreen({ title, endpoint, fields, blank, columns, formatRow }) {
  const { items, save, del } = useCrud(endpoint);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);

  return (
    <div data-testid={`page-admin-${endpoint}`} className="max-w-6xl">
      <div className="flex items-center justify-between mb-8">
        <div><div className="text-[10px] uppercase tracking-[0.28em] text-[#7A736C] mb-2">Content</div><h1 className="font-serif text-4xl tracking-tight">{title}</h1></div>
        <button onClick={() => { setEdit({ ...blank }); setOpen(true); }} data-testid={`${endpoint}-new`} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#A85C42] text-white text-xs uppercase tracking-[0.2em] rounded-sm hover:bg-[#2B2927]"><Plus size={14} /> New</button>
      </div>
      <div className="bg-white border border-[#D5D0C8] rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#EAE6DF] text-xs uppercase tracking-[0.2em] text-[#7A736C]"><tr>{columns.map((c) => <th key={c} className="text-left px-4 py-3">{c}</th>)}<th></th></tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} data-testid={`${endpoint}-row-${it.id}`} className="border-t border-[#EAE6DF]">
                {formatRow(it).map((cell, i) => <td key={i} className="px-4 py-3">{cell}</td>)}
                <td className="px-4 py-3 text-right space-x-2">
                  <button onClick={() => { setEdit({ ...it }); setOpen(true); }} className="text-xs underline hover:text-[#A85C42]" data-testid={`${endpoint}-edit-${it.id}`}>Edit</button>
                  <button onClick={() => { if (window.confirm("Delete?")) del(it.id); }} className="text-[#7A736C] hover:text-red-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={columns.length + 1} className="text-center py-10 text-[#7A736C]">Nothing yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit" : "New"}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            {fields.map((f) => (
              <label key={f.key} className="block">
                <span className="block text-[10px] uppercase tracking-[0.28em] text-[#7A736C] mb-1">{f.label}</span>
                {f.type === "textarea" ? (
                  <textarea rows={f.rows || 3} value={edit?.[f.key] || ""} onChange={(e) => setEdit({ ...edit, [f.key]: e.target.value })} className="input" data-testid={`${endpoint}-field-${f.key}`} />
                ) : f.type === "select" ? (
                  <select value={edit?.[f.key] || ""} onChange={(e) => setEdit({ ...edit, [f.key]: e.target.value })} className="input" data-testid={`${endpoint}-field-${f.key}`}>{f.options.map((o) => <option key={o} value={o}>{o}</option>)}</select>
                ) : f.type === "checkbox" ? (
                  <select value={edit?.[f.key] ? "yes" : "no"} onChange={(e) => setEdit({ ...edit, [f.key]: e.target.value === "yes" })} className="input"><option value="no">No</option><option value="yes">Yes</option></select>
                ) : f.type === "csv" ? (
                  <input value={(edit?.[f.key] || []).join(", ")} onChange={(e) => setEdit({ ...edit, [f.key]: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="input" data-testid={`${endpoint}-field-${f.key}`} />
                ) : (
                  <input type={f.type || "text"} value={edit?.[f.key] || ""} onChange={(e) => setEdit({ ...edit, [f.key]: f.type === "number" ? parseInt(e.target.value) || 0 : e.target.value })} className="input" data-testid={`${endpoint}-field-${f.key}`} />
                )}
              </label>
            ))}
            <style>{`.input{background:white;border:1px solid #D5D0C8;padding:8px 10px;width:100%;font-size:13px;border-radius:3px;outline:none}.input:focus{border-color:#A85C42}`}</style>
          </div>
          <DialogFooter>
            <button onClick={() => { save(edit); setOpen(false); }} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#A85C42] text-white text-xs uppercase tracking-[0.2em] rounded-sm" data-testid={`${endpoint}-save`}><Save size={14} /> Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AdminServices() {
  return <CrudScreen
    title="Services" endpoint="services"
    blank={{ title: "", icon: "Home", short_description: "", description: "", process: [], deliverables: [], images: [], faqs: [], status: "published", order: 0, seo_title: "", seo_description: "" }}
    columns={["Title", "Icon", "Status", "Order"]}
    formatRow={(it) => [it.title, it.icon, it.status, it.order]}
    fields={[
      { key: "title", label: "Title" },
      { key: "icon", label: "Icon (lucide name)" },
      { key: "short_description", label: "Short description", type: "textarea", rows: 2 },
      { key: "description", label: "Full description", type: "textarea", rows: 5 },
      { key: "process", label: "Process steps (comma)", type: "csv" },
      { key: "deliverables", label: "Deliverables (comma)", type: "csv" },
      { key: "images", label: "Image URLs (comma)", type: "csv" },
      { key: "order", label: "Order", type: "number" },
      { key: "status", label: "Status", type: "select", options: ["draft", "published", "archived"] },
      { key: "seo_title", label: "SEO title" },
      { key: "seo_description", label: "SEO description", type: "textarea", rows: 2 },
    ]} />;
}

export function AdminBlog() {
  return <CrudScreen
    title="Blog" endpoint="blog"
    blank={{ title: "", excerpt: "", content: "", cover_image: "", author: "Hiraya Studio", category: "Insights", tags: [], status: "draft" }}
    columns={["Title", "Category", "Author", "Status"]}
    formatRow={(it) => [it.title, it.category, it.author, it.status]}
    fields={[
      { key: "title", label: "Title" },
      { key: "excerpt", label: "Excerpt", type: "textarea", rows: 2 },
      { key: "cover_image", label: "Cover image URL" },
      { key: "author", label: "Author" },
      { key: "category", label: "Category" },
      { key: "tags", label: "Tags (comma)", type: "csv" },
      { key: "content", label: "Content (HTML)", type: "textarea", rows: 10 },
      { key: "status", label: "Status", type: "select", options: ["draft", "published", "archived"] },
      { key: "seo_title", label: "SEO title" },
      { key: "seo_description", label: "SEO description", type: "textarea", rows: 2 },
    ]} />;
}

export function AdminTestimonials() {
  return <CrudScreen
    title="Testimonials" endpoint="testimonials"
    blank={{ author: "", role: "", location: "", quote: "", rating: 5, project_id: "", image: "", featured: false, status: "published" }}
    columns={["Author", "Role", "Location", "Featured"]}
    formatRow={(it) => [it.author, it.role, it.location, it.featured ? "★" : ""]}
    fields={[
      { key: "author", label: "Author" }, { key: "role", label: "Role" }, { key: "location", label: "Location" },
      { key: "quote", label: "Quote", type: "textarea", rows: 4 },
      { key: "image", label: "Image URL" },
      { key: "featured", label: "Featured", type: "checkbox" },
      { key: "status", label: "Status", type: "select", options: ["draft", "published", "archived"] },
    ]} />;
}

export function AdminFAQs() {
  return <CrudScreen
    title="FAQs" endpoint="faqs"
    blank={{ question: "", answer: "", category: "General", order: 0, status: "published" }}
    columns={["Question", "Category", "Order", "Status"]}
    formatRow={(it) => [it.question, it.category, it.order, it.status]}
    fields={[
      { key: "question", label: "Question" },
      { key: "answer", label: "Answer", type: "textarea", rows: 4 },
      { key: "category", label: "Category" },
      { key: "order", label: "Order", type: "number" },
      { key: "status", label: "Status", type: "select", options: ["draft", "published", "archived"] },
    ]} />;
}

export function AdminTeam() {
  return <CrudScreen
    title="Team" endpoint="team"
    blank={{ name: "", role: "", bio: "", image: "", linkedin: "", instagram: "", order: 0, status: "published" }}
    columns={["Name", "Role", "Order", "Status"]}
    formatRow={(it) => [it.name, it.role, it.order, it.status]}
    fields={[
      { key: "name", label: "Name" }, { key: "role", label: "Role" },
      { key: "bio", label: "Bio", type: "textarea", rows: 4 },
      { key: "image", label: "Image URL" }, { key: "linkedin", label: "LinkedIn URL" }, { key: "instagram", label: "Instagram URL" },
      { key: "order", label: "Order", type: "number" },
      { key: "status", label: "Status", type: "select", options: ["draft", "published", "archived"] },
    ]} />;
}

export function AdminInstagram() {
  return <CrudScreen
    title="Instagram imports" endpoint="instagram"
    blank={{ url: "", caption: "", image: "", linked_project_id: "", linked_blog_id: "", status: "pending" }}
    columns={["URL", "Caption", "Status"]}
    formatRow={(it) => [it.url, (it.caption || "").slice(0, 60), it.status]}
    fields={[
      { key: "url", label: "Instagram URL" },
      { key: "image", label: "Thumbnail image URL" },
      { key: "caption", label: "Caption", type: "textarea", rows: 4 },
      { key: "linked_project_id", label: "Linked project ID" },
      { key: "linked_blog_id", label: "Linked blog ID" },
      { key: "status", label: "Status", type: "select", options: ["pending", "approved", "archived"] },
    ]} />;
}
